import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCalendar, FiMic, FiVideo, FiStopCircle, FiCpu } from 'react-icons/fi';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';

const VACIO = { titulo: '', clienteId: '', asignadoA: '', inicio: '', fin: '', estado: 'programada', notas: '' };
const ESTADOS = { programada: 'Programada', confirmada: 'Confirmada', completada: 'Completada', cancelada: 'Cancelada' };
const BADGE = { programada: 'badge-info', confirmada: 'badge-ok', completada: 'badge', cancelada: 'badge-danger' };

const DURACIONES = [
  { value: '15', label: '15 minutos' },
  { value: '30', label: '30 minutos' },
  { value: '45', label: '45 minutos' },
  { value: '60', label: '1 hora' },
  { value: '120', label: '2 horas' },
  { value: '1440', label: '1 día' },
  { value: 'custom', label: 'Personalizada' },
];

const local = (d) => (d ? new Date(d).toISOString().slice(0, 16) : '');

function calcularFinDesdeDuracion(inicioStr, duracionMinutos) {
  if (!inicioStr || !duracionMinutos || duracionMinutos === 'custom') return '';
  const d = new Date(inicioStr);
  if (isNaN(d.getTime())) return '';
  d.setMinutes(d.getMinutes() + Number(duracionMinutos));
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function calcularDuracionDetectada(inicioStr, finStr) {
  if (!inicioStr || !finStr) return '30';
  const d1 = new Date(inicioStr);
  const d2 = new Date(finStr);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 'custom';
  const diffMins = Math.round((d2.getTime() - d1.getTime()) / (60 * 1000));
  if ([15, 30, 45, 60, 120, 1440].includes(diffMins)) {
    return String(diffMins);
  }
  return 'custom';
}

const MAX_MEDIA_BYTES = 25 * 1024 * 1024;
const MIME_MEDIA_PERMITIDOS = [
  'audio/webm', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/m4a', 'audio/x-m4a', 'audio/mp4',
  'video/webm', 'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/3gpp'
];
const MIME_POR_EXTENSION = {
  webm: 'audio/webm', wav: 'audio/wav', mp3: 'audio/mpeg', ogg: 'audio/ogg', m4a: 'audio/m4a', mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo', '3gp': 'video/3gpp'
};

function validarMedia(file, nombre) {
  if (!file) return { ok: false, error: 'Graba o selecciona un archivo de audio o vídeo primero.' };
  if (file.size > MAX_MEDIA_BYTES) return { ok: false, error: 'El archivo supera el máximo permitido (25 MB).' };
  let mimeType = (file.type || '').split(';')[0].trim().toLowerCase();
  if (!MIME_MEDIA_PERMITIDOS.includes(mimeType)) mimeType = MIME_POR_EXTENSION[((nombre || '').split('.').pop() || '').toLowerCase()] || mimeType;
  if (!MIME_MEDIA_PERMITIDOS.includes(mimeType)) return { ok: false, error: 'Formato no soportado. Usa webm, wav, mp3, mp4, ogg, m4a, mov o avi.' };
  return { ok: true, mimeType };
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function CitaVoz({ onAplicar, onCerrar, clientes = [], equipo = [] }) {
  const [archivo, setArchivo] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [mediaUrl, setMediaUrl] = useState(null);
  const [esVideo, setEsVideo] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const [modoGrabando, setModoGrabando] = useState('audio');
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [borrador, setBorrador] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const asignarArchivo = (file, nombre) => {
    if (!file) return;
    if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    setArchivo(file);
    setNombreArchivo(nombre);
    setMediaUrl(URL.createObjectURL(file));
    setEsVideo(file.type?.startsWith('video/') || false);
    setError('');
    setBorrador(null);
  };

  const iniciarGrabacion = async (conVideo = false) => {
    setError('');
    setModoGrabando(conVideo ? 'vídeo' : 'audio');
    try {
      const constraints = conVideo ? { audio: true, video: { width: 640, height: 480 } } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const tipos = conVideo
        ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
        : ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];
      const mimeType = tipos.find((tipo) => window.MediaRecorder.isTypeSupported?.(tipo));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (evento) => { if (evento.data.size) chunksRef.current.push(evento.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const tipoFinal = recorder.mimeType || (conVideo ? 'video/webm' : 'audio/webm');
        asignarArchivo(new Blob(chunksRef.current, { type: tipoFinal }), `cita-grabacion.webm`);
      };
      recorderRef.current = recorder;
      recorder.start();
      setGrabando(true);
    } catch (_err) {
      setError(`No se pudo acceder a los dispositivos (${conVideo ? 'cámara/micrófono' : 'micrófono'}). Revisa los permisos.`);
    }
  };

  const detenerGrabacion = () => {
    recorderRef.current?.stop();
    setGrabando(false);
  };

  const procesar = async () => {
    const validacion = validarMedia(archivo, nombreArchivo);
    if (!validacion.ok) { setError(validacion.error); return; }
    setCargando(true);
    setError('');
    try {
      setMensaje('Transcribiendo audio/vídeo...');
      const audio = await toBase64(archivo);
      const { data: transcripcion } = await api.post('/presupuestos-ia/transcribir-cita', { audio, mimeType: validacion.mimeType });
      setMensaje('Extrayendo datos de la cita con IA...');
      const { data } = await api.post('/presupuestos-ia/generar-cita-desde-voz', { texto: transcripcion.texto });
      setBorrador(data);
      setMensaje('Revisa los datos antes de aplicarlos.');
    } catch (errProcesar) {
      setError(errProcesar?.response?.data?.error || errProcesar?.message || 'No se pudo procesar el archivo.');
    } finally {
      setCargando(false);
    }
  };

  const cerrar = () => {
    if (grabando) recorderRef.current?.stop();
    if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    onCerrar();
  };

  const coincidirCliente = (nombreBuscado) => {
    if (!nombreBuscado || !clientes.length) return '';
    const norm = nombreBuscado.toLowerCase().trim();
    const coincide = clientes.find((c) => c.nombre?.toLowerCase().includes(norm) || norm.includes(c.nombre?.toLowerCase()));
    return coincide?._id || '';
  };

  const coincidirUsuario = (nombreBuscado) => {
    if (!nombreBuscado || !equipo.length) return '';
    const norm = nombreBuscado.toLowerCase().trim();
    const coincide = equipo.find((u) => `${u.nombre} ${u.apellidos || ''}`.toLowerCase().includes(norm) || norm.includes(u.nombre?.toLowerCase()));
    return coincide?._id || '';
  };

  const aplicarBorrador = () => {
    if (!borrador?.datos) return;
    const d = borrador.datos;
    const clienteId = coincidirCliente(d.clienteNombre);
    const asignadoA = coincidirUsuario(d.asignadoNombre);

    onAplicar({
      titulo: d.titulo || 'Cita de trabajo',
      clienteId,
      asignadoA,
      inicio: d.inicio || '',
      fin: d.fin || '',
      notas: d.notas || '',
      estado: d.estado || 'programada',
    });
  };

  return (
    <div>
      <p className="muted">Di los datos de la cita o graba un vídeo explicativo. La IA extraerá cliente, fecha, hora y notas automáticamente.</p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {!grabando ? (
          <>
            <button type="button" className="btn" onClick={() => iniciarGrabacion(false)} disabled={cargando}>
              <FiMic /> Grabar audio
            </button>
            <button type="button" className="btn" onClick={() => iniciarGrabacion(true)} disabled={cargando}>
              <FiVideo /> Grabar vídeo
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-danger" onClick={detenerGrabacion}>
            <FiStopCircle /> Detener grabación ({modoGrabando})
          </button>
        )}
        <label className="btn">
          <FiPlus /> Subir audio / vídeo
          <input type="file" accept="audio/*,video/*" hidden disabled={grabando || cargando} onChange={(e) => asignarArchivo(e.target.files?.[0], e.target.files?.[0]?.name)} />
        </label>
      </div>

      {mediaUrl ? (
        esVideo ? (
          <video controls src={mediaUrl} style={{ width: '100%', maxHeight: 260, borderRadius: 8, marginBottom: 12 }}>Tu navegador no soporta reproducción de vídeo.</video>
        ) : (
          <audio controls src={mediaUrl} style={{ width: '100%', marginBottom: 12 }}>Tu navegador no soporta reproducción de audio.</audio>
        )
      ) : null}

      {mensaje ? <div className="alert-ok">{mensaje}</div> : null}
      {error ? <div className="alert-error">{error}</div> : null}

      {borrador ? (
        <div className="table-wrap" style={{ marginTop: 14, padding: 14 }}>
          <strong><FiCpu /> Cita detectada por IA</strong>
          <p className="muted">Campos detectados: {borrador.camposDetectados?.join(', ') || 'ninguno'}</p>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {Object.entries(borrador.datos || {}).map(([campo, valor]) => (
              <label className="field" key={campo}><span>{campo}</span><input value={valor || ''} onChange={(e) => setBorrador((prev) => ({ ...prev, datos: { ...prev.datos, [campo]: e.target.value || null } }))} /></label>
            ))}
          </div>
          <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} onClick={aplicarBorrador}>
            Aplicar cita al formulario
          </button>
        </div>
      ) : null}

      <div className="form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn" onClick={cerrar}>Cerrar</button>
        <button type="button" className="btn btn-primary" onClick={procesar} disabled={!archivo || cargando || grabando}>
          {cargando ? 'Procesando...' : 'Transcribir y preparar cita'}
        </button>
      </div>
    </div>
  );
}

export default function Citas() {
  const { usuario } = useAuth();
  const { items, cargando, error, crear, actualizar, eliminar } = useEntity('citas');
  const [modal, setModal] = useState(false);
  const [modalVoz, setModalVoz] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [duracion, setDuracion] = useState('30');
  const [guardando, setGuardando] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [equipo, setEquipo] = useState([]);
  const [notificacion, setNotificacion] = useState(null);

  const puedeCrear = puede(usuario, 'citas', 'crear');
  const puedeEditar = puede(usuario, 'citas', 'editar');
  const puedeEliminar = puede(usuario, 'citas', 'eliminar');

  useEffect(() => {
    api.get('/clientes').then(({ data }) => setClientes(data)).catch(() => {});
    api.get('/usuarios').then(({ data }) => setEquipo(data.filter((u) => u.rol !== 'superadmin'))).catch(() => {});
  }, []);

  const mostrarNotificacion = (tipo, texto, duracionMs = 3200) => {
    setNotificacion({ tipo, texto });
    if (duracionMs > 0) {
      window.setTimeout(() => setNotificacion((actual) => (actual?.texto === texto ? null : actual)), duracionMs);
    }
  };

  const abrirNuevo = () => {
    setEditando(null);
    const ahoraStr = local(new Date());
    const finDefault = calcularFinDesdeDuracion(ahoraStr, '30');
    setForm({ ...VACIO, inicio: ahoraStr, fin: finDefault });
    setDuracion('30');
    setModal(true);
  };

  const abrirEditar = (c) => {
    setEditando(c);
    const inStr = local(c.inicio);
    const fnStr = local(c.fin);
    const durDet = calcularDuracionDetectada(inStr, fnStr);
    setForm({ ...VACIO, ...c, clienteId: c.clienteId?._id || c.clienteId || '', asignadoA: c.asignadoA?._id || c.asignadoA || '', inicio: inStr, fin: fnStr });
    setDuracion(durDet);
    setModal(true);
  };

  const onChangeInicio = (e) => {
    const nuevoInicio = e.target.value;
    setForm((f) => {
      const nuevoFin = (duracion !== 'custom' && nuevoInicio)
        ? calcularFinDesdeDuracion(nuevoInicio, duracion)
        : f.fin;
      return { ...f, inicio: nuevoInicio, fin: nuevoFin };
    });
  };

  const onChangeDuracion = (e) => {
    const nuevaDur = e.target.value;
    setDuracion(nuevaDur);
    if (nuevaDur !== 'custom' && form.inicio) {
      const nuevoFin = calcularFinDesdeDuracion(form.inicio, nuevaDur);
      setForm((f) => ({ ...f, fin: nuevoFin }));
    }
  };

  const onChangeFin = (e) => {
    const nuevoFin = e.target.value;
    const durDet = calcularDuracionDetectada(form.inicio, nuevoFin);
    setDuracion(durDet);
    setForm((f) => ({ ...f, fin: nuevoFin }));
  };

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = { ...form, clienteId: form.clienteId || null, asignadoA: form.asignadoA || null };
      if (editando) await actualizar(editando._id, datos);
      else await crear(datos);
      setModal(false);
      mostrarNotificacion('success', editando ? 'Cita actualizada correctamente' : 'Cita creada correctamente');
    } catch (err) { mostrarNotificacion('error', err.response?.data?.error || 'No se pudo guardar la cita'); }
    finally { setGuardando(false); }
  };

  const onEliminar = async (c) => {
    if (!window.confirm(`¿Eliminar la cita "${c.titulo}"?`)) return;
    try { await eliminar(c._id); mostrarNotificacion('success', 'Cita eliminada'); } catch { mostrarNotificacion('error', 'No se pudo eliminar'); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—');
  const nombreCli = (c) => (typeof c === 'object' && c ? c.nombre : '—');
  const nombreUsu = (u) => (typeof u === 'object' && u ? `${u.nombre} ${u.apellidos || ''}`.trim() : '—');

  return (
    <div className="page">
      <div className="page-head">
        <div><h1 className="page-title">Citas</h1><p className="page-subtitle">{items.length} cita(s) en agenda</p></div>
        {puedeCrear && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn" onClick={() => setModalVoz(true)}><FiMic /> Cita por voz / vídeo</button>
            <button type="button" className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nueva cita</button>
          </div>
        )}
      </div>

      <div className="page-help-card" role="status">
        <div>
          <strong>Agenda</strong>
          <p>Asigna clientes y responsables para que cada cita sea más fácil de seguir desde el día a día.</p>
        </div>
        <div className="page-summary">
          <span className="page-pill">Citas: {items.length}</span>
        </div>
      </div>

      {notificacion && (
        <div className={`alert-${notificacion.tipo}`} role="status" style={{ marginBottom: 14 }}>
          {notificacion.texto}
        </div>
      )}

      {error && <div className="alert-error">{error}</div>}
      {cargando ? <p className="empty">Cargando…</p> : items.length === 0 ? <p className="empty"><FiCalendar /> No hay citas.</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Cita</th><th>Cliente</th><th>Asignada a</th><th>Inicio</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c._id}>
                  <td data-label="Cita"><strong>{c.titulo}</strong></td>
                  <td data-label="Cliente">{nombreCli(c.clienteId)}</td>
                  <td data-label="Asignada">{nombreUsu(c.asignadoA)}</td>
                  <td data-label="Inicio">{fmt(c.inicio)}</td>
                  <td data-label="Estado"><span className={`badge ${BADGE[c.estado]}`}>{ESTADOS[c.estado]}</span></td>
                  <td className="acciones">
                    {puedeEditar && <button className="icon-btn" onClick={() => abrirEditar(c)}><FiEdit2 /></button>}
                    {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(c)}><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar cita' : 'Nueva cita'}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field span-2"><span>Título *</span><input required value={form.titulo} onChange={set('titulo')} /></label>
          <label className="field"><span>Cliente</span>
            <select value={form.clienteId} onChange={set('clienteId')}>
              <option value="">Sin cliente</option>
              {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre}</option>)}
            </select>
          </label>
          <label className="field"><span>Asignar a</span>
            <select value={form.asignadoA} onChange={set('asignadoA')}>
              <option value="">Sin asignar</option>
              {equipo.map((u) => <option key={u._id} value={u._id}>{u.nombre} {u.apellidos}</option>)}
            </select>
          </label>
          <label className="field"><span>Inicio *</span><input type="datetime-local" required value={form.inicio} onChange={onChangeInicio} /></label>
          <label className="field"><span>Duración</span>
            <select value={duracion} onChange={onChangeDuracion}>
              {DURACIONES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </label>
          <label className="field span-2"><span>Fin *</span><input type="datetime-local" required value={form.fin} onChange={onChangeFin} /></label>
          <label className="field span-2"><span>Estado</span>
            <select value={form.estado} onChange={set('estado')}>
              {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="field span-2"><span>Notas</span><textarea rows={2} value={form.notas} onChange={set('notas')} /></label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>

      <Modal abierto={modalVoz} onCerrar={() => setModalVoz(false)} titulo="Cita por voz o vídeo" ancho={720}>
        <CitaVoz
          clientes={clientes}
          equipo={equipo}
          onCerrar={() => setModalVoz(false)}
          onAplicar={(datos) => {
            setEditando(null);
            const durDet = calcularDuracionDetectada(datos.inicio, datos.fin);
            setDuracion(durDet);
            setForm({ ...VACIO, ...datos });
            setModalVoz(false);
            setModal(true);
          }}
        />
      </Modal>
    </div>
  );
}
